import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { delay, map, Observable, of, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { APIResponse } from 'src/app/core/models/api-response';
import { IGetConversationsParams, IConversation, IConversationDetails, IConversationUpdate, ConversationClassification, ConversationStatus, IConversationCustomerCatalog, IConversationCustomerProduct, ISentMessage, IGetSentMessagesParams, ICreateOrderAck, IMessageClassificationUpdate, IMessageClassificationResult, IThreadUrgencyResult, IThreadLabelUpdate, IThreadLabelsResult } from '../models/conversation.models';
import { ITimelineOrder } from '../models/timeline.models';
import { IComposePanel } from '../models/compose.models';
import { toHttpParams } from '../utils/http-params.util';
import { buildReplyEditorBody } from '../utils/email-body-sanitizer.util';
import { IProduct } from '../../produits/models/products.models';
import { ICatalog } from '../../catalogs/models/catalogs.models';
import { InboxFolderType } from '../models/inbox.models';

/**
 * Handles conversation CRUD, compose/reply API calls, and attachment downloads.
 */
@Injectable({ providedIn: 'root' })
export class ConversationService {

  // ── Public properties ──────────────────────────────────────────────────────

  /** When true, methods return mock data instead of calling the API. */
  useMockData = environment.useMockData ?? false;

  // ── Private properties ─────────────────────────────────────────────────────

  private backendUrl = environment.BASE_URL;
  private baseUrl = environment.BASE_URL_API + 'inbox';
  private conversationsCache = new Map<number, IConversation[]>();
  /** Mock mode only: how many orders have been "created" per thread this session. */
  private mockCreatedOrders = new Map<number, number>();

  // ── Constructor ────────────────────────────────────────────────────────────

  constructor(private http: HttpClient) {}

  // ── Conversations ─────────────────────────────────────────────────────────

  getConversations(folder: InboxFolderType, params: IGetConversationsParams): Observable<APIResponse<IConversation>> {
    const page = params.page ?? 1;
    const limit = params.limit ?? 50;

    const httpParams = toHttpParams({
      page, limit,
      mailbox_ids:    params.mailbox_ids,
      categories:     params.categories,
      counterparties: params.counterparties,
      statuses:       params.statuses,
      assignee_ids:   params.assignee_ids,
      // Hand-maintained literal: a key absent from HERE never reaches the wire, however
      // correct `buildConversationListParams` is. The old `0`-vs-`undefined` hazard is
      // gone with the scalar; that is the remaining one. The deprecated scalar `team_id`
      // is deliberately NOT sent — the backend consults it only when `team_ids` is
      // absent, and sending both invites the wrong branch to win.
      team_ids:       params.team_ids,
      search_term:    params.search_term?.trim(),
      from:           params.from,
      to:             params.to,
      subject:        params.subject,
      includes_words: params.includes_words,
      excludes_words: params.excludes_words,
      date_from:      params.date_from,
      date_to:        params.date_to,
      has_attachment:  params.has_attachment,
      customer_ids:   params.customer_ids,
      applied_label_ids: params.applied_label_ids,
      is_urgent:      params.is_urgent,
    });

    if (this.useMockData) {
      return of(null).pipe(
        delay(1000),
        switchMap(() => {
          const inboxId = (params.mailbox_ids ?? [])[0] ?? 1;
          const allConversations = inboxId === 1 ? this.getOrCreateMockConversations(inboxId) : [];
          const total = allConversations.length;
          const safeLimit = Math.max(1, limit);
          const pages = Math.max(1, Math.ceil(total / safeLimit));
          const safePage = Math.max(1, Math.min(page, pages));
          const startIndex = (safePage - 1) * safeLimit;
          const endIndex = startIndex + safeLimit;
          const pageConversations = allConversations.slice(startIndex, endIndex);
          return of<APIResponse<IConversation>>({
            data: pageConversations,
            object: undefined,
            message: 'Success',
            metadata: { page: safePage, limit: safeLimit, pages, count: total },
          });
        }),
      );
    }
    return this.http.get<APIResponse<IConversation>>(`${this.baseUrl}/threads/${folder}`, { params: httpParams });
  }

  /**
   * List sent (outbound) messages for the Sent folder. Unlike getConversations,
   * this returns a flat list of messages (one row per sent message), not threads.
   */
  getSentMessages(params: IGetSentMessagesParams): Observable<APIResponse<ISentMessage>> {
    const page = params.page ?? 1;
    const limit = params.limit ?? 50;

    const httpParams = toHttpParams({
      page, limit,
      mailbox_ids:    params.mailbox_ids,
      search_term:    params.search_term?.trim(),
      date_from:      params.date_from,
      date_to:        params.date_to,
      has_attachment: params.has_attachment,
    });

    return this.http.get<APIResponse<ISentMessage>>(`${this.baseUrl}/messages/sent`, { params: httpParams });
  }

  getConversationDetails(conversationId: number, commentId?: number): Observable<APIResponse<IConversationDetails>> {
    if (this.useMockData) {
      const commentIdForMock = commentId ?? 1;
      return of(null).pipe(
        delay(1000),
        switchMap(() =>
          of<APIResponse<IConversationDetails>>({
            data: [],
            object: {
              id: conversationId,
              subject: 'Subject 1',
              provider_thread_id: 'thread-1',
              last_message_at: '2026-01-21T14:55:00Z',
              message_count: 2,
              attachment_count: 0,
              is_read: false,
              assigned_to_id: '1',
              urgency: 'low',
              classification: ConversationClassification.ORDER,
              status: ConversationStatus.OPEN,
              assignee: { id: 1, name: 'John Doe', email: 'john.doe@example.com', avatar: 'https://via.placeholder.com/150', active: true, type: 'user' },
              mailbox: { id: 1, name: 'Inbox 1', email: 'inbox1@example.com', type: 'personal' },
              sender: 'Sender 1',
              sender_email: 'sender1@example.com',
              customer: { id: 1, company_name: 'Company 1', location: 'Location 1' },
              objects: [
                {
                  id: 1, subject: 'Subject 1', sender: 'Sender 1', sender_name: 'Sender 1',
                  sender_email: 'sender1@example.com', recipients: 'inbox1@example.com',
                  cc: '', bcc: '', timestamp: '2026-01-21T14:55:00Z', thread_id: conversationId,
                  is_read: false, has_attachments: false, attachment_count: 0,
                  body: 'This is a plain text body',
                  object_type: 'message',
                },
                {
                  id: 2, subject: 'Subject 2', sender: 'Sender 2', sender_name: 'Sender 2',
                  sender_email: 'sender2@example.com', recipients: 'inbox1@example.com',
                  cc: '', bcc: '', timestamp: '2026-01-21T14:30:00Z', thread_id: conversationId,
                  is_read: false, has_attachments: false, attachment_count: 0,
                  body: '<p>This is an <strong>example</strong> of an HTML email body. <br>Here is a <a href="https://example.com">link</a> and some <em>styled</em> text.</p>',
                  object_type: 'message',
                },
                {
                  id: commentIdForMock,
                  object_type: 'comment',
                  author: { id: 3, name: 'John Doe', email: 'john.doe@example.com' },
                  body: 'This is a comment',
                  created_at: '2026-01-21T14:30:00Z',
                  mentions: [
                    { id: 1, name: 'John Doe', email: 'john.doe@example.com' },
                    { id: 2, name: 'Jane Doe', email: 'jane.doe@example.com' },
                  ],
                  my_reactions: [],
                  reactions: [],
                  thread_id: conversationId,
                  updated_at: '2026-01-21T14:30:00Z',
                },
                // One pre-existing order so the artifact's created state is visible
                // on load, plus one per mock creation triggered in this session.
                ...[0, ...this.mockCreatedOrderCounts(conversationId)].map((index) =>
                  this.buildMockTimelineOrder(conversationId, index)
                ),
              ],
              thread_context: {
                company_name: 'Company 1',
                sender: {
                  email: 'sender1@example.com',
                  name: 'Sender 1',
                  is_contact: true,
                },
                kpis: {
                  total_orders_amount: 1000,
                  average_order_value: 100,
                  average_sku_count_per_order: 10,
                },
                last_orders: [],
                products_kpis: {
                  favorite_brand: 'Brand 1',
                  products_bought_count: 10,
                },
              },
            },
            message: 'Success',
            metadata: { page: 1, limit: 10, pages: 1, count: 0 },
          }),
        ),
      );
    }
    const params = commentId != null ? toHttpParams({ comment_id: commentId }) : undefined;
    return this.http.get<APIResponse<IConversationDetails>>(`${this.baseUrl}/threads/${conversationId}`, { params });
  }

  getThreadCustomerCatalogs(conversationId: number, params: { page: number; limit: number; keyword: string }): Observable<APIResponse<IConversationCustomerCatalog>> {
    const httpParams = toHttpParams({
      page: params.page,
      limit: params.limit,
      keyword: params.keyword,
    });
    if (this.useMockData) {
      return of(null).pipe(
        delay(1000),
        switchMap(() =>
          of<APIResponse<IConversationCustomerCatalog>>({
            data: [
              {
                id: 1,
                name: 'Catalog 1',
                image: 'https://via.placeholder.com/150',
                nbr_products: 10,
              },
            ], object: undefined, message: 'Success',
            metadata: { page: 1, limit: 10, pages: 1, count: 0 },
          }),
        ),
      );
    }
    return this.http.get<APIResponse<IConversationCustomerCatalog>>(`${this.baseUrl}/threads/${conversationId}/catalogs`, { params: httpParams });
  }

  getThreadCustomerProducts(conversationId: number, params: { page: number; limit: number; keyword: string }): Observable<APIResponse<IConversationCustomerProduct>> {
    const httpParams = toHttpParams({
      page: params.page,
      limit: params.limit,
      keyword: params.keyword,
    });

    if (this.useMockData) {
      return of(null).pipe(
        delay(1000),
        switchMap(() =>
          of<APIResponse<IConversationCustomerProduct>>({
            data: [
              {
                product_tmpl_id: 1,
                product_id: 1,
                pricelist_id: 1,
                name: 'Product 1',
                sku: '1234567890',
                barcode: '1234567890',
                sales_price: 100,
                available_quantity: 10,
                brand: 'Brand 1',
              },
            ], object: undefined, message: 'Success',
            metadata: { page: 1, limit: 10, pages: 1, count: 0 },
          }),
        ),
      );
    }

    return this.http.get<APIResponse<IConversationCustomerProduct>>(`${this.baseUrl}/threads/${conversationId}/catalog-products`, { params: httpParams });
  }

  updateConversations(conversationIds: number[], payload: IConversationUpdate): Observable<APIResponse<IConversationDetails>> {
    if (payload.status !== undefined && payload.status === 'new') {
      payload = { ...payload, status: null } as unknown as IConversationUpdate;
    }
    if (this.useMockData) {
      return of(null).pipe(
        delay(1000),
        switchMap(() =>
          of<APIResponse<IConversationDetails>>({
            data: [], object: undefined, message: 'Success',
            metadata: { page: 1, limit: 10, pages: 1, count: 0 },
          }),
        ),
      );
    }
    return this.http.put<APIResponse<IConversationDetails>>(`${this.baseUrl}/threads/update`, { thread_ids: conversationIds, ...payload });
  }

  markConversationsRead(conversationIds: number[], isRead: boolean): Observable<APIResponse<IConversation>> {
    if (this.useMockData) {
      return of(null).pipe(
        delay(1000),
        switchMap(() =>
          of<APIResponse<IConversation>>({
            data: [], object: undefined, message: 'Success',
            metadata: { page: 1, limit: 10, pages: 1, count: 0 },
          }),
        ),
      );
    }
    return this.http.put<APIResponse<IConversation>>(`${this.baseUrl}/threads/mark-read`, { thread_ids: conversationIds, is_read: isRead });
  }

  // ── Classification & urgency overrides ─────────────────────────────────────

  /**
   * Override the AI's category and/or intent on a single message. The thread's
   * `classification`, `categories` and `is_urgent` are recomputed server-side,
   * so callers must re-fetch the thread to render the new rollup.
   */
  updateMessageClassification(messageId: number, payload: IMessageClassificationUpdate): Observable<APIResponse<IMessageClassificationResult>> {
    if (this.useMockData) {
      return of(null).pipe(
        delay(500),
        switchMap(() =>
          of<APIResponse<IMessageClassificationResult>>({
            data: [],
            object: {
              id: messageId,
              classification: payload.category ?? ConversationClassification.UNDEFINED,
              intent: payload.intent ?? null,
              counterparty: payload.counterparty ?? null,
            },
            message: 'Success',
            metadata: { page: 1, limit: 10, pages: 1, count: 0 },
          }),
        ),
      );
    }
    return this.http.put<APIResponse<IMessageClassificationResult>>(`${this.baseUrl}/messages/${messageId}/classification`, payload);
  }

  /**
   * Set the operator's urgency decision on a thread. It sticks: later AI-urgent
   * messages will not flip the thread back. Message-level urgency is untouched.
   */
  updateThreadUrgency(conversationId: number, isUrgent: boolean): Observable<APIResponse<IThreadUrgencyResult>> {
    if (this.useMockData) {
      return of(null).pipe(
        delay(500),
        switchMap(() =>
          of<APIResponse<IThreadUrgencyResult>>({
            data: [],
            object: { id: conversationId, is_urgent: isUrgent },
            message: 'Success',
            metadata: { page: 1, limit: 10, pages: 1, count: 0 },
          }),
        ),
      );
    }
    return this.http.put<APIResponse<IThreadUrgencyResult>>(`${this.baseUrl}/threads/${conversationId}/urgency`, { is_urgent: isUrgent });
  }

  /**
   * Apply and remove labels on one conversation in a single call.
   *
   * The response carries the thread's whole visible label set afterwards, so the
   * caller replaces its list with `object.labels` rather than patching what it
   * sent: a `create_and_apply` that resolved to an existing label comes back as
   * one entry, and another operator's concurrent change arrives with it.
   *
   * No mock branch, unlike the sibling methods: mock mode has no label
   * catalogue, and answering an empty list would render as "every label was
   * removed" after every action — a lie that looks like a working feature.
   */
  updateThreadLabels(conversationId: number, payload: IThreadLabelUpdate): Observable<APIResponse<IThreadLabelsResult>> {
    return this.http.put<APIResponse<IThreadLabelsResult>>(`${this.baseUrl}/threads/${conversationId}/labels`, payload);
  }

  // ── Compose / Reply / Forward / Send Draft ─────────────────────────────────────────────

  compose(composePanel: IComposePanel): Observable<any> {
    if (this.useMockData) {
      return of(null).pipe(delay(1000), switchMap(() => of({ message: 'Success' })));
    }

    const fullBody = this.buildFullBody(composePanel);

    const payload: Record<string, unknown> = {
      to: composePanel.to ? composePanel.to.split(',') : [],
      cc: composePanel.cc ? composePanel.cc.split(',') : [],
      bcc: composePanel.bcc ? composePanel.bcc.split(',') : [],
      subject: composePanel.subject,
      body: fullBody,
      body_type: 'html',
    };

    if(composePanel.draftId) {
      return this.http.post<any>(`${this.baseUrl}/drafts/${composePanel.draftId}/send`, payload);
    }

    if (composePanel.type === 'reply' || composePanel.type === 'reply-all' || composePanel.type === 'forward') {
      if (!composePanel.reply_to_conversation) {
        console.error('Conversation is required for reply/reply-all/forward');
        return of(null).pipe(delay(1000), switchMap(() => of({ message: 'Conversation is required for reply/reply-all/forward' })));
      }

      if (!composePanel.reply_to_message && composePanel.type === 'forward') {
        console.error('Message is required for forward');
        return of(null).pipe(delay(1000), switchMap(() => of({ message: 'Message is required for forward' })));
      }

      const conversationId = composePanel.reply_to_conversation.id;
      const messageId = composePanel.reply_to_message?.id;

      if (composePanel.type === 'reply' || composePanel.type === 'reply-all') {
        const replyPayload: Record<string, unknown> = {
          subject: composePanel.subject,
          body: fullBody,
          body_type: 'html',
          reply_all: composePanel.type === 'reply-all',
        };
        return this.http.post<any>(`${this.baseUrl}/threads/${conversationId}/reply`, replyPayload);
      }

      if (composePanel.type === 'forward') {
        return this.http.post<any>(`${this.baseUrl}/threads/${conversationId}/messages/${messageId}/forward`, payload);
      }
    }

    return this.http.post<any>(`${this.baseUrl}/mailboxes/${composePanel.from?.id}/send`, payload);
  }

  // ── Create Order ──────────────────────────────────────────────────────────

  /**
   * Ask the backend to create an order from the conversation.
   *
   * Answers 202 immediately — matching runs in the background (~2 min) and
   * completion arrives as an `order_created` realtime event, so the body is
   * only an acknowledgement, never the finished order.
   *
   * Failure modes worth distinguishing: `409` a creation is already running for
   * this thread, `503` the queue was full so nothing started (safe to retry).
   */
  createOrder(conversationId: number): Observable<APIResponse<ICreateOrderAck>> {
    if (this.useMockData) {
      this.mockCreatedOrders.set(conversationId, (this.mockCreatedOrders.get(conversationId) ?? 0) + 1);
      return of<APIResponse<ICreateOrderAck>>({
        data: [],
        object: { thread_id: conversationId, status: 'processing' },
        message: 'Success',
        metadata: { page: 1, limit: 10, pages: 1, count: 0 },
      }).pipe(delay(500));
    }
    return this.http.post<APIResponse<ICreateOrderAck>>(`${this.baseUrl}/threads/${conversationId}/create-order`, {});
  }

  // ── Mock helpers ────────────────────────────────────────────────────────────

  /** Indices of the orders "created" for a thread in mock mode (1-based). */
  private mockCreatedOrderCounts(conversationId: number): number[] {
    const count = this.mockCreatedOrders.get(conversationId) ?? 0;
    return Array.from({ length: count }, (_, i) => i + 1);
  }

  /**
   * A timeline order shaped the way WB-7444 will serve it — `lines_preview` capped
   * at 5 with a higher `total_order_lines`, so the artifact's `+N more` link and
   * handling-unit quantities are both exercised without a backend.
   */
  private buildMockTimelineOrder(conversationId: number, index: number): ITimelineOrder {
    const suffix = index === 0 ? '41A6' : `41A${6 + index}`;
    const states = ['draft', 'to_be_validated', 'sent', 'done'];
    return {
      object_type: 'order',
      id: 900 + index,
      thread_id: conversationId,
      created_at: '2026-01-21T15:10:00Z',
      updated_at: '2026-01-21T15:10:00Z',
      author: { id: 1, name: 'John Doe', email: 'john.doe@example.com' },
      order: {
        id: 4200 + index,
        name: `ORD-${suffix}`,
        state: states[index % states.length],
        created_at: '2026-01-21T15:10:00Z',
        last_modified: '2026-01-21T15:42:00Z',
        customer_name: 'Bruno Distributions',
        total: 336.94,
        currency: { name: 'EUR', symbol: '€', position: 'after' },
        total_order_lines: 8,
        company_name: 'Bruno Distributions',
        location_name: 'Warehouse A',
        // One row the AI could not match, leading the table: the card offers
        // Review instead of Approve order, and the row renders '--' in both
        // pricing columns behind the alert icon.
        unmatched_lines_count: 1,
        lines_preview: [
          { id: 58, product_name: 'FORA 6 Duo meters', sku: 'LGF6DUO', quantity: 100, quantity_handling_unit: null, handling_unit_name: null, final_price: null, total: null, unmatched: true },
          { id: 1, product_name: 'Tube PVC-U PN16 Ø63 6 m', sku: 'APP-PVC-63', quantity: 8, quantity_handling_unit: null, handling_unit_name: null, final_price: 29.88, total: 239.04 },
          { id: 2, product_name: 'Coude 90° FF Ø63', sku: 'APP-CD9-63', quantity: 12, quantity_handling_unit: 2, handling_unit_name: 'BOX', final_price: 4.10, total: 49.20 },
          { id: 3, product_name: 'Colle PVC pression 500 ml', sku: 'APP-COL-500', quantity: 1, quantity_handling_unit: null, handling_unit_name: null, final_price: 15.10, total: 15.10 },
          { id: 4, product_name: 'Té égal FF Ø63', sku: 'APP-TE-63', quantity: 4, quantity_handling_unit: null, handling_unit_name: null, final_price: 6.40, total: 25.60 },
          { id: 5, product_name: 'Manchon FF Ø63', sku: 'APP-MAN-63', quantity: 3, quantity_handling_unit: null, handling_unit_name: null, final_price: 2.67, total: 8.00 },
        ],
      },
    };
  }

  // ── Add Sender Contact ──────────────────────────────────────────────────────

  /** Add the thread sender's email to the matched company's email registry. */
  addSenderToContacts(conversationId: number): Observable<APIResponse<IConversationDetails>> {
    if (this.useMockData) {
      return of(null).pipe(
        delay(1000),
        switchMap(() =>
          of<APIResponse<IConversationDetails>>({
            data: [], object: undefined, message: 'Success',
            metadata: { page: 1, limit: 10, pages: 1, count: 0 },
          }),
        ),
      );
    }
    return this.http.post<APIResponse<IConversationDetails>>(`${this.baseUrl}/threads/${conversationId}/add-sender-contact`, {});
  }

  /**
   * Returns the body to send. For reply/reply-all, if the user never expanded
   * the quote toggle, appends the quoted original so the outgoing email
   * includes thread context. For forward, the editor already has it.
   */
  private buildFullBody(panel: IComposePanel): string {
    const body = panel.body || '';
    const isReply = panel.type === 'reply' || panel.type === 'reply-all';

    if (isReply && panel.reply_to_message && !panel.quoteAppended) {
      return body + this.stripQuoteSpacers(buildReplyEditorBody(panel.reply_to_message));
    }

    return this.stripQuoteSpacers(body);
  }

  /** Remove the empty lines added before the quote for editor UX. */
  private stripQuoteSpacers(html: string): string {
    return html.replace(/(<p>&nbsp;<\/p>)+(<div class="gmail_quote">)/i, '$2');
  }

  // ── Attachments ───────────────────────────────────────────────────────────

  downloadAttachment(download_url: string, filename: string = 'attachment', context?: HttpContext): Observable<{ blob: Blob; filename: string; contentType: string }> {
    return this.http.get(`${this.backendUrl}${download_url}`, {
      responseType: 'blob',
      observe: 'response',
      context,
    }).pipe(
      map((res) => {
        const blob = res.body ?? new Blob();
        const contentType = res.headers.get('Content-Type') ?? 'application/octet-stream';
        return { blob, filename, contentType };
      }),
    );
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private getOrCreateMockConversations(inboxId: number): IConversation[] {
    const cached = this.conversationsCache.get(inboxId);
    if (cached) return cached;

    const totalConversations = 250;
    const now = Date.now();
    const urgencies = ['low', 'medium', 'high'];

    const conversations: IConversation[] = Array.from({ length: totalConversations }, (_, idx) => {
      const id = idx + 1;
      const minutesAgo = id * 7;
      const last_message_at = new Date(now - minutesAgo * 60 * 1000).toISOString();

      return {
        id,
        subject: `Thread ${id} (Inbox ${inboxId}): Lorem Ipsum is simply dummy text of the printing and typesetting industry.`,
        sender: `Sender ${id}`,
        sender_email: `sender${id}@example.com`,
        mailbox: { id: inboxId, name: `Inbox ${inboxId}`, email: `inbox${inboxId}@example.com` },
        provider_thread_id: `thread-${id}`,
        last_message_at,
        message_count: (id % 17) + 1,
        attachment_count: (id % 3) + 1,
        is_read: id % 3 === 0,
        assigned_to_id: `${(id % 5) + 1}`,
        urgency: urgencies[id % urgencies.length],
        classification: Object.values(ConversationClassification)[id % Object.values(ConversationClassification).length] as ConversationClassification,
        status: Object.values(ConversationStatus)[id % Object.values(ConversationStatus).length] as ConversationStatus,
        assignee: null,
        customer: { id: id, company_name: `Company ${id}`, location: `Location ${id}` },
      };
    });

    this.conversationsCache.set(inboxId, conversations);
    return conversations;
  }
}
